/* eslint-disable max-lines-per-function */
import { inject, Injectable } from '@angular/core';

import {
  AdditionalUserSelectionOption,
  AdditionalUserSelectionOptionKey,
  User,
} from '@core/account-types';
import { I18nService } from '@core/i18n-legacy';
import { AutomationTriggerType } from '@core/workflow-automations-types';

@Injectable({ providedIn: 'root' })
export class AdditionalUserSelectionOptionsService {
  private readonly i18n = inject(I18nService);

  private readonly defaultOptions: AdditionalUserSelectionOption[] = [
    {
      key: AdditionalUserSelectionOptionKey.AdminIdsAddRecruiter,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AddRecruiter,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AdminIdsAddHiringManager,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AddHiringManager,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AdminIdsAddAdminPerformingAction,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AddAdminPerformingAction,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AddInterviewers,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.interviewers.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.interviewers.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.interviewers.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AdminIdsAddInterviewers,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.interviewers.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.interviewers.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.interviewers.tooltip',
      ),
      isSelected: false,
    },
  ];

  public getOptions({
    withoutBadge,
    action,
    trigger,
    recruiter,
    hiringManager,
    hasWorkflowAutomationImprovements,
  }: {
    withoutBadge?: boolean;
    trigger?: {
      type: AutomationTriggerType | null;
    };
    action: {
      adminIdsAddHiringManager?: boolean;
      addHiringManager?: boolean;
      adminIdsAddRecruiter?: boolean;
      addRecruiter?: boolean;
      adminIdsAddAdminPerformingAction?: boolean;
      addAdminPerformingAction?: boolean;
      addInterviewers?: boolean;
      adminIdsAddInterviewers?: boolean;
    };
    recruiter: User | null;
    hiringManager: User | null;
    hasWorkflowAutomationImprovements?: boolean;
  }): AdditionalUserSelectionOption[] {
    return this.getDefaultOptions(action, trigger, hasWorkflowAutomationImprovements).map(
      option => {
        if (option.key === AdditionalUserSelectionOptionKey.AdminIdsAddHiringManager) {
          return {
            ...option,
            isSelected: !!action.adminIdsAddHiringManager,
            badge:
              !withoutBadge && !hiringManager
                ? {
                    icon: 'warning',
                    color: 'warning-tint',
                    label: this.i18n.translate('app.common.not_assigned'),
                  }
                : undefined,
          };
        }

        if (option.key === AdditionalUserSelectionOptionKey.AddHiringManager) {
          return {
            ...option,
            isSelected: !!action.addHiringManager,
            badge:
              !withoutBadge && !hiringManager
                ? {
                    icon: 'warning',
                    color: 'warning-tint',
                    label: this.i18n.translate('app.common.not_assigned'),
                  }
                : undefined,
          };
        }

        if (option.key === AdditionalUserSelectionOptionKey.AdminIdsAddRecruiter) {
          return {
            ...option,
            isSelected: !!action.adminIdsAddRecruiter,
            badge:
              !withoutBadge && !recruiter
                ? {
                    icon: 'warning',
                    color: 'warning-tint',
                    label: this.i18n.translate('app.common.not_assigned'),
                  }
                : undefined,
          };
        }

        if (option.key === AdditionalUserSelectionOptionKey.AddRecruiter) {
          return {
            ...option,
            isSelected: !!action.addRecruiter,
            badge:
              !withoutBadge && !recruiter
                ? {
                    icon: 'warning',
                    color: 'warning-tint',
                    label: this.i18n.translate('app.common.not_assigned'),
                  }
                : undefined,
          };
        }

        if (option.key === AdditionalUserSelectionOptionKey.AdminIdsAddAdminPerformingAction) {
          return {
            ...option,
            isSelected: !!action.adminIdsAddAdminPerformingAction,
          };
        }

        if (option.key === AdditionalUserSelectionOptionKey.AddAdminPerformingAction) {
          return {
            ...option,
            isSelected: !!action.addAdminPerformingAction,
          };
        }

        if (option.key === AdditionalUserSelectionOptionKey.AddInterviewers) {
          return {
            ...option,
            isSelected: !!action.addInterviewers,
          };
        }

        if (option.key === AdditionalUserSelectionOptionKey.AdminIdsAddInterviewers) {
          return {
            ...option,
            isSelected: !!action.adminIdsAddInterviewers,
          };
        }

        return {
          ...option,
          isSelected: false,
          showNoAssignedBadge: false,
        };
      },
    );
  }

  private getDefaultOptions(
    action: {
      adminIdsAddHiringManager?: boolean;
      addHiringManager?: boolean;
      adminIdsAddRecruiter?: boolean;
      addRecruiter?: boolean;
      adminIdsAddAdminPerformingAction?: boolean;
      addAdminPerformingAction?: boolean;
      addInterviewers?: boolean;
      adminIdsAddInterviewers?: boolean;
    },
    trigger?: {
      type: AutomationTriggerType | null;
    },
    hasWorkflowAutomationImprovements?: boolean,
  ): AdditionalUserSelectionOption[] {
    const isEventRelatedTrigger = trigger?.type?.includes('event');
    return this.defaultOptions
      .filter(option => action[option.key] !== undefined)
      .filter(option =>
        option.key === AdditionalUserSelectionOptionKey.AddInterviewers ||
        option.key === AdditionalUserSelectionOptionKey.AdminIdsAddInterviewers
          ? isEventRelatedTrigger && hasWorkflowAutomationImprovements
          : true,
      );
  }
}
